import { useEffect } from 'react';
import { infiniteQueryOptions, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { getMarketingBulkSms, useGetMarketingBulkSms } from '@/api/generated/marketing/marketing';
import type { GetMarketingBulkSmsParams } from '@/api/generated/schemas';
import { activeSendPollInterval } from '@/features/clients/text-clients/use-active-bulk-send';
import { getApiErrorDetails } from '@/lib/error-utils';
import { marketingQueryKeys } from '../use-marketing-queries';
import type { BulkSendChannel, BulkSmsHistoryItem } from './bulk-sms-types';

/** The Texts & Emails page's channel filter as it reads in the URL (`?channel=texts`); none = All. */
export type BulkSendsHistoryFilter = 'texts' | 'emails';
export const BULK_SENDS_HISTORY_FILTERS: readonly BulkSendsHistoryFilter[] = ['texts', 'emails'];

const FILTER_CHANNEL: Record<BulkSendsHistoryFilter, BulkSendChannel> = { texts: 'SMS', emails: 'EMAIL' };

export function bulkSendsHistoryChannel(filter: BulkSendsHistoryFilter | undefined): BulkSendChannel | undefined {
  return filter ? FILTER_CHANNEL[filter] : undefined;
}

/** 400 for a cursor the list no longer shows (its row left this filter): the next-page link went stale. */
function isStaleCursorError(error: unknown) {
  return getApiErrorDetails(error).code === 'CURSOR_NOT_AVAILABLE';
}

/** The app's default 3 retries, except a stale cursor: it never heals, so reset at once instead of spinning. */
const MAX_RETRIES = 3;

/**
 * Every non-draft send, most recently sent first, 20 per page (the API's default). Shared by the page and its nav
 * prefetcher. `orgRouteParam` is the URL's `$orgId` (salon short code), which the bulk-send keys use.
 */
export const bulkSendsHistoryInfiniteQueryOptions = (orgRouteParam: string, channel: BulkSendChannel | undefined) =>
  infiniteQueryOptions({
    queryKey: marketingQueryKeys.bulkSendsHistory(orgRouteParam, channel),
    queryFn: ({ pageParam, signal }) => {
      const params: GetMarketingBulkSmsParams = { ...(channel ? { channel } : {}), ...(pageParam ? { cursor: pageParam } : {}) };
      return getMarketingBulkSms(Object.keys(params).length > 0 ? params : undefined, signal);
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    retry: (failureCount, error) => !isStaleCursorError(error) && failureCount < MAX_RETRIES,
  });

/** One page's rows with any fresher copy of the same send swapped in. */
function withFreshRows<T extends { data: BulkSmsHistoryItem[] }>(page: T, fresh: ReadonlyMap<string, BulkSmsHistoryItem>): T {
  return { ...page, data: page.data.map((row) => fresh.get(row.id) ?? row) };
}

/**
 * The Texts & Emails list. A stale cursor ("Load more" after the list moved under it) quietly drops back to page 1
 * instead of showing an error. Chips and counts stay live without re-reading every loaded page: the newest-20 list
 * (the Clients "Sending" pill's read, polled only while a send is active) patches the same sends in place.
 */
export function useBulkSendsHistory(orgRouteParam: string, filter: BulkSendsHistoryFilter | undefined) {
  const queryClient = useQueryClient();
  const channel = bulkSendsHistoryChannel(filter);
  const query = useInfiniteQuery(bulkSendsHistoryInfiniteQueryOptions(orgRouteParam, channel));
  const stale = query.isError && isStaleCursorError(query.error);
  const live = useGetMarketingBulkSms(undefined, {
    query: {
      queryKey: marketingQueryKeys.bulkSends(orgRouteParam),
      refetchInterval: (latest) => activeSendPollInterval(latest.state.data),
    },
  });
  const liveRows = live.data?.data;

  useEffect(() => {
    if (!liveRows) return;
    const fresh = new Map(liveRows.map((row) => [row.id, row]));
    const { queryKey } = bulkSendsHistoryInfiniteQueryOptions(orgRouteParam, channel);
    queryClient.setQueryData(queryKey, (data) => data && { ...data, pages: data.pages.map((page) => withFreshRows(page, fresh)) });
  }, [liveRows, queryClient, orgRouteParam, channel]);

  useEffect(() => {
    if (!stale) return;
    const { queryKey } = bulkSendsHistoryInfiniteQueryOptions(orgRouteParam, channel);
    queryClient.setQueryData(queryKey, (data) => data && { pages: data.pages.slice(0, 1), pageParams: data.pageParams.slice(0, 1) });
    void queryClient.invalidateQueries({ queryKey, exact: true });
  }, [stale, queryClient, orgRouteParam, channel]);

  const rows = query.data?.pages.flatMap((page) => page.data) ?? [];
  return { query, rows, loadMoreFailed: query.isFetchNextPageError && !stale };
}
